import { ReactElement, useMemo, useCallback } from 'react';
import { useSnackbar } from 'notistack';

import { useSubscriptionFeatures, useLazyRequest } from '~hooks/index';
import { updateItemPreset } from '~services/item';
import { ItemPreset, ItemPresetParse } from '~services/item/types';
import { OrdersListFilters } from '~hooks/useOrdersListFilters';
import { DialogConfimation } from '~components/index';
import { convertOrderListFilterToItemFilter } from '~utils/order';
import { camelCaseSerializer } from '~utils/serializer';
import { PlanFeaturesTypes } from '~globals/types/enums';

interface OrdersFiltersPresetsDialogConfirmEditProps {
  open: boolean;
  onClose: () => void;
  selectedPreset: ItemPresetParse;
  currentFilters: OrdersListFilters;
  onSuccess: (currentPreset: ItemPresetParse) => void;
}

const OrdersFiltersPresetsDialogConfirmEdit = ({
  open,
  onClose,
  selectedPreset,
  currentFilters,
  onSuccess,
}: OrdersFiltersPresetsDialogConfirmEditProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const messageConfirmation = useMemo(
    () => `El filtro ${selectedPreset.description} será actualizado`,
    [selectedPreset.description],
  );

  const [, loadingEditPreset, , executeEditPreset] = useLazyRequest({
    request: updateItemPreset,
    withPostSuccess: (response) => {
      const presetResponse = response.data?.data as ItemPreset;
      const parsePresetResponse: ItemPresetParse = {
        ...presetResponse,
        data: camelCaseSerializer.serialize(JSON.parse(presetResponse.data)),
      };
      const message = `El filtro ${parsePresetResponse.description} fue actualizado correctamente`;

      onSuccess(parsePresetResponse);

      enqueueSnackbar(message, { variant: 'success' });
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo actualizado el filtro, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const handleEditPreset = useCallback(() => {
    const data = JSON.stringify(
      convertOrderListFilterToItemFilter(currentFilters, {
        limitNoPro: hasAccessToFeature(PlanFeaturesTypes.LimitNotPro),
        mgmZones: hasAccessToFeature(PlanFeaturesTypes.MgmZones),
      }),
    );

    executeEditPreset({
      presetId: selectedPreset.presetId,
      description: selectedPreset.description,
      data,
    });
  }, [
    currentFilters,
    executeEditPreset,
    hasAccessToFeature,
    selectedPreset.description,
    selectedPreset.presetId,
  ]);

  return (
    <DialogConfimation
      open={open}
      description={messageConfirmation}
      onConfirm={handleEditPreset}
      onCancel={onClose}
      loadingConfirm={loadingEditPreset}
    />
  );
};

export default OrdersFiltersPresetsDialogConfirmEdit;
